import { ChartLegend } from '@/common/components/ChartLgeend'
import { ChartContainer } from '@/common/components/ui/chart'
import { ChartData } from '@/common/types/chart.type'
import { cn } from '@/common/utils/cn'
import { Bar, BarChart as BarChartComponent, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import CustomChartTooltip, { CustomChartLabel } from '../ui/tooltip'
import { EmptyStateMessage } from '../EmptyStateMessage'

export interface BarChartProps {
  chartData: ChartData
  className?: string
}

export const BarChart = ({ chartData, className }: BarChartProps) => {
  const legend =
    Object.keys(chartData.series_settings).length > 1 &&
      Object.values(chartData.series_settings).some(({ label }) => label)
      ? Object.keys(chartData.series_settings).map((key) => ({
        label: chartData.series_settings[key].label,
        color: chartData.series_settings[key].color,
      }))
      : []

  return (
    <div
      className={cn('w-full bg-white-200 rounded-lg p-0 overflow-hidden')}
      style={{ gridColumn: `span ${chartData.width}` }}
    >
      {chartData.kind !== 'value' && (
        <div className="flex flex-col w-full gap-2 bg-white-200 p-4 pt-6">
          <div className="flex flex-col gap-1">
            <p className="font-bold text-2xl">{chartData.title}</p>
            {chartData.subtitle && <p className="font-light text-[10px] leading-3">{chartData.subtitle}</p>}
          </div>

          {!!legend?.length && <ChartLegend legend={legend} />}
        </div>
      )}

      <ChartContainer config={chartData.series_settings} minHeight={200} className={cn('min-h-[200px] w-full', className)}>
        {!chartData.data.length ? (
          <EmptyStateMessage />
        ) : (
          <BarChartComponent
            data={chartData.data}
            {...{
              overflow: 'visible',
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={chartData.primary.query_identifier} tickMargin={4} angle={-25} interval={0} textAnchor="end" height={60} className='text-[10px]'
              tickFormatter={(value: string) =>
                value.length > 25 ? `${value.slice(0, 25)}...` : value
              } />
            <YAxis />
            <Tooltip content={(props) => (
              <CustomChartTooltip {...props} seriesSettings={chartData.series_settings} />
            )} />
            {Object.values(chartData.series_settings).map(({ query_identifier, color, format_parser }) => {
              return (
                <Bar key={query_identifier} dataKey={query_identifier} stackId={undefined} style={{ fill: color }}
                  label={<CustomChartLabel format_parser={format_parser} />}
                />
              )
            })}
          </BarChartComponent>
        )}
      </ChartContainer>
    </div>
  )
}
